import { Injectable, inject, signal } from '@angular/core';
import { Session } from '@supabase/supabase-js';
import { DATA_CLIENT } from './auth.service';

/**
 * The signed-in identity as the **data** project sees it.
 *
 * AuthService.session() deliberately prefers the Azure/cloud session, because that is
 * where the role claims live. Chat cannot use it: the chat tables and their RLS policies
 * are in the data project, so `auth.uid()` — and the bearer token the chat API validates —
 * must come from the data client. In production the two clients are the same object and
 * the distinction costs nothing. In development they are different projects, and getting
 * this wrong makes every policy fail with an empty result rather than an error.
 *
 * Both values are kept current by onAuthStateChange, never an awaited getSession().
 * navigator.sendBeacon on tab close cannot await anything, and neither should a header.
 *
 * The user id is a signal because the chat bubble mounts at the app root, before
 * anyone has signed in, and has to start itself when the session finally arrives.
 * The token stays a plain field: the beacon reads it from a handler that cannot
 * await, and nothing needs to react to it.
 */
@Injectable({ providedIn: 'root' })
export class ChatIdentityService {
  private readonly client = inject(DATA_CLIENT);

  private accessToken: string | null = null;
  private readonly uid = signal<string | null>(null);

  constructor() {
    void this.client.auth.getSession().then(({ data }) => this.adopt(data.session));
    this.client.auth.onAuthStateChange((_event, session) => this.adopt(session));
  }

  token(): string | null { return this.accessToken; }

  userId(): string | null { return this.uid(); }

  private adopt(session: Session | null): void {
    this.accessToken = session?.access_token ?? null;
    this.uid.set(session?.user?.id ?? null);
  }
}
